import React, { useEffect, useState } from 'react'
import { BlogPost } from '../../types'

const BlogManagement: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null)
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    featuredImage: '',
    status: 'draft' as 'draft' | 'published',
    seoTitle: '',
    seoDescription: '',
    category: '',
    tags: [] as string[],
    customCss: ''
  })

  useEffect(() => {
    fetchPosts()
  }, [])

  const fetchPosts = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api'
      const response = await fetch(`${apiUrl}/blog/admin/all`)
      const data = await response.json()

      if (data.success) {
        setPosts(data.data)
      }
    } catch (error) {
      console.error('Error fetching posts:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api'
      const url = editingPost
        ? `${apiUrl}/blog/admin/${editingPost._id}`
        : `${apiUrl}/blog/admin`

      const method = editingPost ? 'PUT' : 'POST'

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })

      const data = await response.json()

      if (data.success) {
        alert(editingPost ? 'Beitrag aktualisiert!' : 'Beitrag erstellt!')
        fetchPosts()
        resetForm()
      }
    } catch (error) {
      console.error('Error saving post:', error)
      alert('Fehler beim Speichern')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Beitrag wirklich löschen?')) return

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api'
      const response = await fetch(`${apiUrl}/blog/admin/${id}`, {
        method: 'DELETE'
      })

      const data = await response.json()

      if (data.success) {
        alert('Beitrag gelöscht!')
        fetchPosts()
      }
    } catch (error) {
      console.error('Error deleting post:', error)
    }
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      alert('Nur Bilddateien sind erlaubt (JPEG, PNG, GIF, WEBP)')
      return
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('Datei ist zu groß. Maximal 5MB erlaubt.')
      return
    }

    setUploading(true)

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api'
      const formDataUpload = new FormData()
      formDataUpload.append('image', file)

      const response = await fetch(`${apiUrl}/upload/blog-image`, {
        method: 'POST',
        body: formDataUpload
      })

      const data = await response.json()

      if (data.success) {
        // Cloudinary gibt vollständige URL zurück
        const imageUrl = data.data.url
        setFormData({ ...formData, featuredImage: imageUrl })
        alert(`Bild erfolgreich hochgeladen! URL: ${imageUrl}`)
      } else {
        alert(data.error || 'Fehler beim Hochladen')
      }
    } catch (error) {
      console.error('Upload error:', error)
      alert('Fehler beim Hochladen des Bildes')
    } finally {
      setUploading(false)
    }
  }

  const resetForm = () => {
    setEditingPost(null)
    setFormData({
      title: '',
      slug: '',
      content: '',
      excerpt: '',
      featuredImage: '',
      status: 'draft',
      seoTitle: '',
      seoDescription: '',
      category: '',
      tags: [],
      customCss: ''
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Lade Blogbeiträge...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold mb-6">Blog-Verwaltung</h1>

      {/* Form */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-bold mb-4">
          {editingPost ? 'Beitrag bearbeiten' : 'Neuer Beitrag'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block font-medium mb-2">Titel</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border rounded"
              required
            />
          </div>

          <div>
            <label className="block font-medium mb-2">Slug</label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              className="w-full px-4 py-2 border rounded"
              placeholder="wird automatisch generiert"
            />
          </div>

          <div>
            <label className="block font-medium mb-2">Kurzbeschreibung (Excerpt)</label>
            <textarea
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              className="w-full px-4 py-2 border rounded"
              rows={3}
              required
            />
          </div>

          <div>
            <label className="block font-medium mb-2">Inhalt (HTML)</label>
            <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
              <strong>💡 Tipp:</strong> Nutze HTML-Tags für schöne Formatierung:
              <ul className="mt-1 ml-4 list-disc text-xs space-y-1">
                <li><code>&lt;h2&gt;Überschrift&lt;/h2&gt;</code> - Große Überschrift</li>
                <li><code>&lt;h3&gt;Unterüberschrift&lt;/h3&gt;</code> - Kleinere Überschrift</li>
                <li><code>&lt;p&gt;Text&lt;/p&gt;</code> - Absatz</li>
                <li><code>&lt;strong&gt;Fett&lt;/strong&gt;</code> - Fettgedruckter Text</li>
                <li><code>&lt;ul&gt;&lt;li&gt;Punkt 1&lt;/li&gt;&lt;/ul&gt;</code> - Aufzählung</li>
                <li><code>&lt;a href="URL"&gt;Link&lt;/a&gt;</code> - Link</li>
                <li><code>&lt;img src="URL" alt="Text" /&gt;</code> - Bild im Text</li>
                <li className="mt-2"><strong>Info-Boxen:</strong></li>
                <li><code>&lt;div class="info-box"&gt;💡 Tipp&lt;/div&gt;</code> - Blaue Info-Box</li>
                <li><code>&lt;div class="warning-box"&gt;⚠️ Achtung&lt;/div&gt;</code> - Gelbe Warn-Box</li>
                <li><code>&lt;div class="success-box"&gt;✅ Erfolg&lt;/div&gt;</code> - Grüne Erfolgs-Box</li>
                <li><code>&lt;div class="tip-box"&gt;💎 Profi-Tipp&lt;/div&gt;</code> - Lila Tipp-Box</li>
              </ul>
            </div>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              className="w-full px-4 py-2 border rounded font-mono text-sm"
              rows={15}
              required
              placeholder="<h2>Überschrift</h2>
<p>Ihr Text hier...</p>
<ul>
  <li>Punkt 1</li>
  <li>Punkt 2</li>
</ul>"
            />
          </div>

          <div>
            <label className="block font-medium mb-2">Featured Image</label>

            {/* Image Preview */}
            {formData.featuredImage && (
              <div className="mb-3">
                <img
                  src={formData.featuredImage}
                  alt="Preview"
                  className="max-w-xs h-40 object-cover rounded border"
                />
              </div>
            )}

            {/* Upload Button */}
            <div className="flex items-center space-x-3 mb-2">
              <label className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 cursor-pointer inline-block">
                {uploading ? 'Hochladen...' : '📁 Bild hochladen'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploading}
                  className="hidden"
                />
              </label>
              <span className="text-sm text-gray-500">Max. 5MB (JPEG, PNG, GIF, WEBP)</span>
            </div>

            {/* Manual URL Input */}
            <div>
              <label className="block text-sm text-gray-600 mb-1">Oder URL manuell eingeben:</label>
              <input
                type="url"
                value={formData.featuredImage}
                onChange={(e) => setFormData({ ...formData, featuredImage: e.target.value })}
                className="w-full px-4 py-2 border rounded"
                placeholder="https://example.com/image.jpg"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-medium mb-2">Kategorie</label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-2 border rounded"
              />
            </div>

            <div>
              <label className="block font-medium mb-2">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                className="w-full px-4 py-2 border rounded"
              >
                <option value="draft">Entwurf</option>
                <option value="published">Veröffentlicht</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block font-medium mb-2">SEO Titel</label>
            <input
              type="text"
              value={formData.seoTitle}
              onChange={(e) => setFormData({ ...formData, seoTitle: e.target.value })}
              className="w-full px-4 py-2 border rounded"
              maxLength={60}
            />
          </div>

          <div>
            <label className="block font-medium mb-2">SEO Beschreibung</label>
            <textarea
              value={formData.seoDescription}
              onChange={(e) => setFormData({ ...formData, seoDescription: e.target.value })}
              className="w-full px-4 py-2 border rounded"
              rows={2}
              maxLength={160}
            />
          </div>

          <div>
            <label className="block font-medium mb-2">CSS Design (Optional)</label>
            <div className="mb-2 p-3 bg-indigo-50 border border-indigo-200 rounded text-sm text-indigo-800">
              <strong>🎨 Info:</strong> Hier kannst du eigenes CSS für diesen Beitrag definieren.
              <ul className="mt-1 ml-4 list-disc text-xs space-y-1">
                <li>Beispiel für Info-Boxen, Custom-Styles, etc.</li>
                <li>CSS-Code wird nur auf dieser Seite angewendet</li>
              </ul>
            </div>
            <textarea
              value={formData.customCss}
              onChange={(e) => setFormData({ ...formData, customCss: e.target.value })}
              className="w-full px-4 py-2 border rounded font-mono text-sm"
              rows={10}
              placeholder=".info-box {
  background-color: #eff6ff;
  border-left: 4px solid #3b82f6;
  padding: 1rem;
  margin: 1rem 0;
}

.warning-box {
  background-color: #fef3c7;
  border-left: 4px solid #f59e0b;
  padding: 1rem;
  margin: 1rem 0;
}"
            />
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
            >
              {editingPost ? 'Aktualisieren' : 'Erstellen'}
            </button>

            {editingPost && (
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-600 text-white px-6 py-2 rounded hover:bg-gray-700"
              >
                Abbrechen
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Posts List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4">Alle Beiträge ({posts.length})</h2>

          {posts.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Noch keine Blogbeiträge vorhanden.</p>
          ) : (
            <div className="space-y-4">
              {posts.map((post) => (
                <div key={post._id} className="border p-4 rounded flex justify-between items-center">
                  <div>
                    <h3 className="font-bold">{post.title}</h3>
                    <p className="text-sm text-gray-600">
                      {post.status === 'published' ? '✅ Veröffentlicht' : '📝 Entwurf'} •{' '}
                      {post.category} •{' '}
                      {post.views} Aufrufe
                    </p>
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setEditingPost(post)
                        setFormData({
                          title: post.title,
                          slug: post.slug,
                          content: post.content,
                          excerpt: post.excerpt,
                          featuredImage: post.featuredImage || '',
                          status: post.status,
                          seoTitle: post.seoTitle,
                          seoDescription: post.seoDescription,
                          category: post.category,
                          tags: post.tags,
                          customCss: post.customCss || ''
                        })
                        window.scrollTo({ top: 0, behavior: 'smooth' })
                      }}
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                      Bearbeiten
                    </button>

                    <button
                      onClick={() => handleDelete(post._id)}
                      className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
                    >
                      Löschen
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default BlogManagement
